import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { formatInTz } from "../../lib/dates";
import { ChevronLeft, ChevronRight, Repeat } from "lucide-react";

const REC_STEPS = {
  daily: { amount: 1, unit: "day" },
  weekly: { amount: 1, unit: "week" },
  monthly: { amount: 1, unit: "month" },
  yearly: { amount: 1, unit: "year" },
};

/**
 * Expand a recurring task into an array of dayjs occurrences that fall
 * within [rangeStart, rangeEnd] inclusive. Includes the base occurrence
 * only if it falls in the range. Caps iteration at MAX_ITER for safety.
 */
function expandRecurrences(baseIso, recurrence, rangeStart, rangeEnd) {
  const out = [];
  if (!baseIso) return out;
  const step = REC_STEPS[recurrence];
  const base = dayjs(baseIso);
  // Base itself: include if within range
  if (
    (base.isSame(rangeStart) || base.isAfter(rangeStart)) &&
    (base.isBefore(rangeEnd) || base.isSame(rangeEnd, "day"))
  ) {
    out.push({ date: base, isVirtual: false });
  }
  if (!step) return out;
  // Walk forward from base into future occurrences
  let cur = base;
  for (let i = 0; i < 500; i++) {
    cur = cur.add(step.amount, step.unit);
    if (cur.isAfter(rangeEnd)) break;
    if (cur.isBefore(rangeStart)) continue;
    out.push({ date: cur, isVirtual: true });
  }
  // Also walk backward: if the base is IN the future but the current
  // month range is BEFORE it, we may still want to display historical
  // occurrences. Only relevant if base was in the past.
  let back = base;
  for (let i = 0; i < 500; i++) {
    back = back.subtract(step.amount, step.unit);
    if (back.isBefore(rangeStart)) break;
    if (back.isAfter(rangeEnd)) continue;
    out.push({ date: back, isVirtual: true });
  }
  return out;
}

export default function CalendarView({ tasks, tenantTz, onOpen }) {
  const [monthOffset, setMonthOffset] = useState(0);

  const { start, end, days, monthLabel } = useMemo(() => {
    const base = dayjs().add(monthOffset, "month").startOf("month");
    const start = base.startOf("week");
    const end = base.endOf("month").endOf("week");
    const arr = [];
    let cur = start;
    while (cur.isBefore(end) || cur.isSame(end, "day")) {
      arr.push(cur);
      cur = cur.add(1, "day");
    }
    return { start, end, days: arr, monthLabel: base.format("MMMM YYYY") };
  }, [monthOffset]);

  const tasksByDay = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      if (!t.due_at) return;
      const rec = t.recurrence || "none";
      const occurrences = expandRecurrences(t.due_at, rec, start, end);
      occurrences.forEach(({ date, isVirtual }) => {
        const key = date.format("YYYY-MM-DD");
        (map[key] = map[key] || []).push({
          task: t,
          // Compose an occurrence-specific time using base's HH:mm on this day
          displayTime: date.format("HH:mm"),
          isVirtual,
        });
      });
    });
    // Sort entries within a day by time
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => a.displayTime.localeCompare(b.displayTime));
    });
    return map;
  }, [tasks, start, end]);

  return (
    <div className="rounded-lg border border-zinc-200 bg-white" data-testid="calendar-view">
      <div className="flex items-center justify-between border-b border-zinc-100 px-6 py-4">
        <div className="font-display text-lg font-semibold">{monthLabel}</div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setMonthOffset((m) => m - 1)}
            data-testid="calendar-prev-btn"
            className="rounded-md border border-zinc-200 p-1.5 hover:bg-zinc-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => setMonthOffset(0)}
            data-testid="calendar-today-btn"
            className="rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50"
          >
            Today
          </button>
          <button
            onClick={() => setMonthOffset((m) => m + 1)}
            data-testid="calendar-next-btn"
            className="rounded-md border border-zinc-200 p-1.5 hover:bg-zinc-50"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 border-b border-zinc-100 text-[10px] uppercase tracking-wider text-zinc-500">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="border-r border-zinc-100 px-3 py-2 last:border-r-0">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = day.format("YYYY-MM-DD");
          const dayEntries = tasksByDay[key] || [];
          const isToday = day.isSame(dayjs(), "day");
          return (
            <div
              key={key}
              data-testid={`calendar-day-${key}`}
              className="min-h-[100px] border-b border-r border-zinc-100 p-2 last:border-r-0"
            >
              <div
                className={[
                  "mb-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px]",
                  isToday ? "bg-zinc-900 text-white" : "text-zinc-500",
                ].join(" ")}
              >
                {day.date()}
              </div>
              <div className="space-y-1">
                {dayEntries.slice(0, 3).map((entry, i) => (
                  <button
                    key={`${entry.task.id}-${i}`}
                    onClick={() => onOpen(entry.task)}
                    data-testid={`calendar-task-${entry.task.id}${entry.isVirtual ? "-virtual" : ""}`}
                    title={entry.isVirtual ? "Yinelenen — kaynak görevi aç" : entry.task.title}
                    className={[
                      "flex w-full items-center gap-1 truncate rounded px-2 py-1 text-left text-[11px] transition-colors",
                      entry.isVirtual
                        ? "border border-dashed border-purple-200 bg-purple-50/60 text-purple-800 hover:bg-purple-100/70"
                        : "bg-zinc-100 text-zinc-800 hover:bg-zinc-200",
                    ].join(" ")}
                  >
                    {entry.isVirtual && <Repeat className="h-2.5 w-2.5 shrink-0" />}
                    <span className="truncate">
                      {entry.displayTime} · {entry.task.title}
                    </span>
                  </button>
                ))}
                {dayEntries.length > 3 && (
                  <div className="text-[10px] text-zinc-500">
                    +{dayEntries.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
